import { Grid } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import WSDashboardCard from "../../../Components/CardListing/WSDashboardCard";
import WSFilterButtonGroup from "../../../Components/Filter/WSFilterButtonGroup";
import WSButton from "../../../Components/Button/WSButton";
import WSDataGridNew, {
  IColumns,
} from "../../../Components/DataGrid/WSDataGridNew";
import { WSIcons } from "../../../commonFunctions/iIcons";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";
import { getLocalValue, LoacalVariables, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import http from "../../../commonFunctions/httpClient";

interface LabScan {
  id: string;
  labId: string;
  lab: any;
  scanSubcategoryId: string;
  scanSubcategory: {
    id: string;
    name: string;
    description: string;
    alsoKnownAs: string;
    preparationInstructions: string;
    turnaroundTime: string;
    imgUrl: string;
    scancategory: {
      name: string;
    };
  };
  mrpPrice: number;
  discountPercentage: number;
  finalPrice: number;
  turnaroundTime: string;
  createdDate: string;
  updatedDate: string | null;
}

interface SummaryCounts {
  totalServiceCount: number;
  totalScans: number;
  activeScans: number;
  inactiveScans: number;
}

export const ScanTestList: React.FC<any> = React.memo((props) => {
  const navigate = useNavigate();

  const filters = ["All"];
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);
  const [data, setData] = useState<LabScan[]>([]);
  const [filteredData, setFilteredData] = useState<LabScan[]>([]);
  const [labId, setLabId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filterCounts, setFilterCounts] = useState<Record<string, number>>({
    All: 0,
  });

  const [summaryCounts, setSummaryCounts] = useState<SummaryCounts>({
    totalServiceCount: 0,
    totalScans: 0,
    activeScans: 0,
    inactiveScans: 0,
  });

  useEffect(() => {
    updateMainHeader("Scan Tests");
    breadCrumbsStore.updateBreadCrumbs([
      { "Scan Tests": "scantest/list" },
    ]);
  }, []);

  useEffect(() => {
    const userId = getLocalValue(LoacalVariables.UserId);
    setLabId(userId || "");
  }, []);

  const calculateFilterCounts = useCallback((scans: LabScan[]) => {
    console.log("🔥 RAW SCAN DATA:", scans.map(s => ({ name: s.scanSubcategory?.name, id: s.id })));
    
    const counts: Record<string, number> = {
      All: scans.length,
    };
    
    console.log("🔥 CALCULATED FILTER COUNTS:", counts);
    return counts;
  }, []);

  const fetchLabScans = useCallback(async (): Promise<void> => {
    if (!labId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");
      
      const response: any = await http.get(`lab/Labscanning/ByLab/${labId}`);
      const apiData = response.data || [];
      const labScans = Array.isArray(apiData) ? apiData : [];
      
      // Transform data to flatten nested properties for DataGrid
      const transformedData = labScans.map((scan: LabScan) => ({
        ...scan,
        scanName: scan.scanSubcategory?.name || "N/A",
        scanCategory: scan.scanSubcategory?.scancategory?.name || "N/A",
        preparation: scan.scanSubcategory?.preparationInstructions || "N/A",
        finalPriceDisplay: `₹${scan.finalPrice || 0}`
      }));
      
      console.log("🔥 TOTAL SCANS LOADED:", transformedData.length);
      setData(transformedData as any);
      
      // Calculate filter counts
      const counts = calculateFilterCounts(transformedData as LabScan[]);
      setFilterCounts(counts);
      setFilteredData(transformedData as any);
      
    } catch (err: any) {
      console.error("❌ fetchLabScans error:", err);
      setError(err.message || "Failed to fetch scan tests");
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [labId, calculateFilterCounts]);

  const fetchSummaryCounts = useCallback(async (): Promise<void> => {
    if (!labId) return;

    try {
      const activeScans = data.filter(scan => scan.lab?.isActive).length;
      const inactiveScans = data.length - activeScans;
      
      setSummaryCounts({
        totalServiceCount: data.length,
        totalScans: data.length,
        activeScans: activeScans,
        inactiveScans: inactiveScans,
      });
    } catch (err: any) {
      console.error("❌ Summary fetch error:", err);
      setSummaryCounts({
        totalServiceCount: 0,
        totalScans: 0,
        activeScans: 0,
        inactiveScans: 0,
      });
    }
  }, [labId, data]);

  useEffect(() => {
    if (labId) {
      fetchLabScans();
    }
  }, [labId, fetchLabScans]);

  useEffect(() => {
    if (data.length > 0) {
      fetchSummaryCounts();
      const counts = calculateFilterCounts(data as LabScan[]);
      setFilterCounts(counts);
    }
  }, [data, calculateFilterCounts, fetchSummaryCounts]);

  const handleFilterChange = useCallback(
    (value: string) => {
      const originalFilter = value.replace(/\(\d+\)$/, "").trim();
      console.log("🔄 Filter changed to:", originalFilter);
      setSelectedFilter(originalFilter);
      setFilteredData(data);
    },
    [data]
  );

  const getFilterLabel = useCallback(
    (filter: string) => {
      const count = filterCounts[filter] || 0;
      return `${filter}(${count})`;
    },
    [filterCounts]
  );

  const handleAction = async (action: string, row: any) => {
    if (action === "Edit") {
      navigate(`/scan-test/${row?.id}`);
    }
  };

  const handleClick = () => {
    navigate(`/scan-test/add`);
  };

  const columns: IColumns[] = [
    { header: "Scan Name", fieldName: "scanName", type: "default" },
    { header: "Scan Category", fieldName: "scanCategory", type: "default" },
    { header: "MRP Price", fieldName: "mrpPrice", type: "default" },
    { header: "Final Price", fieldName: "finalPriceDisplay", type: "default" },
    { header: "Discount %", fieldName: "discountPercentage", type: "default" },
    { header: "Turn Around Time", fieldName: "turnaroundTime", type: "default" },
    // { header: "Preparation", fieldName: "preparation", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["Edit"],
      actionCallback: (action, row) => handleAction(action, row),
    },
  ];

  if (loading) {
    return (
      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid
          item
          sm={12}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <div>Loading scan tests...</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Total Scan Tests"
            value={summaryCounts.totalScans.toString()}
            percentage="+5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<ShoppingCartIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Active Scans"
            value={summaryCounts.activeScans.toString()}
            percentage="+10%"
            bottomIcon={<TrendingUpIcon />}
            icon={<WSIcons.InStockIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Total Revenue"
            value="₹45,000"
            percentage="+15%"
            bottomIcon={<TrendingUpIcon />}
            icon={<WSIcons.OutOfStockIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Inactive Scans"
            value={summaryCounts.inactiveScans.toString()}
            percentage="-2%"
            bottomIcon={<TrendingUpIcon />}
            icon={<ShoppingCartIcon sx={{ color: "red" }} />}
          />
        </Grid>

        <Grid item sm={9}>
          <WSFilterButtonGroup
            options={filters.map(filter => getFilterLabel(filter))}
            selected={getFilterLabel(selectedFilter)}
            onChange={handleFilterChange}
          />
        </Grid>
        <Grid item sm={3} display="flex" justifyContent="flex-end">
          <WSButton
            id="btnAddNewScanTest"
            label="Add New Scan Category"
            startIcon={<AddIcon />}
            onClick={handleClick}
            sx={{
              borderRadius: "999px",
              backgroundColor: "#098963!important",
              textTransform: "none",
              fontWeight: 500,
              height: "35px!important",
              fontSize: "0.875rem!important",
              paddingX: 3,
              paddingY: 1,
              "&:hover": {
                backgroundColor: "#087654!important",
              },
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid item sm={12}>
          {error ? (
            <div style={{ padding: 20, color: 'red' }}>
              {error}. <button onClick={fetchLabScans}>Retry</button>
            </div>
          ) : (
            <WSDataGridNew
              columns={columns}
              data={filteredData}
              id="scanTestListGrid"
              title={`Scan Tests (${filteredData.length})`}
              exportCsv={true}
              searchBox={{ fieldNames: ["scanName", "scanCategory"] }}
              gridHeaderSx={{
                backgroundColor: "whitesmoke",
                border: "1px solid whitesmoke",
              }}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
});

export default ScanTestList;
